<template>
    <div class="p-3 border-b border-gray-100">
        <!-- Comment header -->
        <div class="flex items-start gap-3">
            <img :src="profilePicture" alt="User Avatar" class="w-8 h-8 rounded-full object-cover border" />
            <div class="flex-1">

                <div class="bg-gray-100 px-3 py-2 rounded-xl">
                    <div class="flex justify-between items-center">
                        <h4 class="font-semibold text-sm text-gray-800">
                            {{ comment.user.name }}
                        </h4>
                        <p class="text-xs text-gray-400">{{ postedAt }}</p>
                    </div>
                    <p class="text-sm text-gray-700 mt-1">
                        {{ comment.comment }}
                    </p>
                </div>

                <!-- Reply button -->
                <div class="flex gap-4 mt-2 text-xs text-gray-500">
                    <span class="cursor-pointer hover:text-blue-500" @click="toggleReplyBox">
                        Reply
                    </span>
                    <span v-if="comment.replies_count > 0 && !allRepliesLoaded && !loadingReplies"
                        class="cursor-pointer hover:text-blue-500" @click="loadReplies">
                        View all {{ comment.replies_count }} replies
                    </span>
                    <!-- <span v-if="!allRepliesLoaded && !loadingReplies" class="cursor-pointer hover:text-blue-500"
                        @click="loadReplies">
                        View replies
                    </span> -->
                    <span v-if="loadingReplies">Loading replies...</span>
                </div>

                <!-- Reply input box -->
                <div v-if="showReplyBox" class="mt-2">
                    <div class="flex p-4 gap-2">
                        <div class="flex-shrink-0 h-8 w-8">
                            <img :src="myProfilePicture" alt="User Avatar"
                                class="w-full h-full rounded-full object-cover border" />
                        </div>
                        <div class="flex-grow bg-gray-100 px-3 py-2 rounded-xl">
                            <textarea v-model="newReply" class="w-full bg-gray-100 resize-none outline-none text-sm"
                                :placeholder="`Reply to ${comment.user.name}`" @keyup.enter="submitReply"></textarea>
                            <div class="flex justify-end bg-gray-100">
                                <div @click.stop="submitReply"
                                    class="h-7 w-7 flex flex-col justify-center items-center rounded-full"
                                    :class="newReply ? 'cursor-pointer hover:bg-gray-300' : ''">
                                    <IconPaperPlaneSolid class="h-4"
                                        :class="newReply ? 'text-blue-600' : 'text-gray-400 cursor-not-allowed'" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Replies list -->
                <div v-if="replies.length" class="mt-3 ml-3 border-l pl-3">
                    <InnovationComment v-for="r in replies" :key="r.id" :comment="r" :is-reply="true"
                        :current-date-time="currentDateTime" :innovation-token="innovationToken"
                        @update-comments-count="updateCommentsCount" />
                    <button v-if="!allRepliesLoaded" @click="loadReplies" class="text-xs text-blue-500 mt-2">
                        Load more replies
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gql from "graphql-tag";
import { timeAgo } from "../../../helpers/utils";
import IconPaperPlaneSolid from "../../../components/icons/IconPaperPlaneSolid.vue";

const ADD_THINK_TANK_COMMENT = gql`
  mutation AddInnovationComment($innovation_token: String!, $comment: String!, $parent_id: ID) {
    addInnovationComment(innovation_token: $innovation_token, comment: $comment, parent_id: $parent_id) {
        error
        error_code
        message
        data {
            id
            comment
            created_at
            user {
                id
                name
                profile_picture_url
            }
        }
    }
  }
`;

const GET_THINK_TANK_COMMENT_REPLIES = gql`
  query GetInnovationCommentReplies($parent_id: ID!, $page: Int!, $perPage: Int!) {
    innovationCommentReplies(parent_id: $parent_id, page: $page, perPage: $perPage) {
      data {
        id
        innovation_id
        parent_id
        comment
        created_at
        updated_at
        user {
          id
          name
          profile_picture_url
        }
        replies_count
        replies {
          id
          comment
        }
      }
      paginatorInfo {
        currentPage
        hasMorePages
        total
      }
    }
  }
`;

export default {
    name: "InnovationComment",
    components: {
        IconPaperPlaneSolid,
    },
    props: {
        comment: { type: Object, required: true },
        isReply: { type: Boolean, default: false },
        currentDateTime: { type: String, required: true },
        innovationToken: { type: String, required: true },
    },
    data() {
        return {
            replies: [],
            replyPage: 1,
            loadingReplies: false,
            allRepliesLoaded: false,
            showReplyBox: false,
            newReply: "",
        };
    },
    computed: {
        profilePicture() {
            return process.env.VUE_APP_PUBLIC_URL + this.comment.user.profile_picture_url;
        },
        postedAt() {
            return timeAgo(this.comment.created_at, this.currentDateTime);
        },
        myProfilePicture() {
            return this.$store.getters.profilePictureUrl;
        },
    },
    methods: {
        toggleReplyBox() {
            this.showReplyBox = !this.showReplyBox;
        },
        async loadReplies() {
            if (this.loadingReplies || this.allRepliesLoaded) return;
            this.loadingReplies = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_THINK_TANK_COMMENT_REPLIES,
                    variables: { parent_id: this.comment.id, page: this.replyPage, perPage: 5 },
                    fetchPolicy: "network-only",
                });
                if (data && data.innovationCommentReplies) {
                    const newReplies = data.innovationCommentReplies.data;
                    this.replies = this.replies.concat(newReplies);
                    this.replyPage++;
                    if (!data.innovationCommentReplies.paginatorInfo.hasMorePages) {
                        this.allRepliesLoaded = true;
                    }
                }
            } catch (e) {
                console.error("Failed to load replies:", e);
            } finally {
                this.loadingReplies = false;
            }
        },
        async submitReply() {
            if (!this.newReply.trim()) return;

            try {

                const { data } = await this.$apollo.mutate({
                    mutation: ADD_THINK_TANK_COMMENT,
                    variables: {
                        innovation_token: this.innovationToken,
                        comment: this.newReply,
                        parent_id: parseInt(this.comment.id),
                    },
                });

                const response = data.addInnovationComment;

                if (response.error) {
                    return this.$swal.fire({
                        icon: 'error',
                        title: 'Oops...',
                        text: response.message,
                    });
                }

                this.replies.unshift(response.data);
                this.newReply = "";
                this.showReplyBox = false;

                this.$emit('update-comments-count', this.innovationToken);

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: error.message || 'Could not add Think Tank comment.',
                });
            }
        },
        updateCommentsCount(token) {
            this.$emit('update-comments-count', token);
        },
    },
}
</script>